import { Link } from 'react-router-dom';
import { ArrowLeft, Bot, Zap, FileCheck, BarChart3, Brain } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { useState, useEffect } from 'react';

const serviceKeys = ['digital_s1','digital_s2','digital_s3','digital_s4','digital_s5'];

const PIPELINE_STEPS = [
  { icon: FileCheck, labelKey: 'eng_v2.pipeline_step1', detailKey: 'eng_v2.pipeline_step1_desc' },
  { icon: Brain, labelKey: 'eng_v2.pipeline_step2', detailKey: 'eng_v2.pipeline_step2_desc' },
  { icon: BarChart3, labelKey: 'eng_v2.pipeline_step3', detailKey: 'eng_v2.pipeline_step3_desc' },
  { icon: Bot, labelKey: 'eng_v2.pipeline_step4', detailKey: 'eng_v2.pipeline_step4_desc' },
];

const AiPipelineAnimation = () => {
  const [activeStep, setActiveStep] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(s => (s + 1) % PIPELINE_STEPS.length);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="py-16 px-4 sm:px-6 lg:px-8" style={{ background: '#0A1F13' }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-emerald-400 mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {t('eng_v2.digital_hero_workflow')}
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tighter mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {t('eng_v2.digital_hero_title')}
        </h3>
        <p className="text-sm text-neutral-400 mb-10 max-w-xl">
          {t('eng_v2.digital_hero_desc')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="ai-pipeline-steps">
          {PIPELINE_STEPS.map((step, i) => {
            const Icon = step.icon;
            const isActive = i === activeStep;
            const isPast = i < activeStep;
            return (
              <div
                key={i}
                className="relative p-6 transition-all duration-500"
                style={{
                  background: isActive ? '#163321' : '#0D2919',
                  border: `1px solid ${isActive ? '#10B981' : '#163321'}`,
                  opacity: isPast || isActive ? 1 : 0.5,
                }}
                data-testid={`pipeline-step-${i}`}
              >
                {i < PIPELINE_STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-px" style={{ background: isPast || isActive ? '#10B981' : '#163321' }} />
                )}
                <Icon
                  className="w-6 h-6 mb-4 transition-colors duration-500"
                  style={{ color: isActive ? '#10B981' : isPast ? '#059669' : '#1B3A2D' }}
                />
                <h4 className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {t(step.labelKey)}
                </h4>
                <p className="text-xs text-neutral-500 leading-relaxed">{t(step.detailKey)}</p>
                {isActive && (
                  <div className="absolute bottom-0 left-0 h-0.5 animate-pulse" style={{ background: '#10B981', width: '100%' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const StudioDigital = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen pt-16" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>
      <SEO
        title="Digital & AI Solutions | Durable Designs"
        description={t('services.digital_intro_1')}
        path="/engineering/services/digital"
      />

      {/* Hero — dark with code-like pattern */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: '#06120B' }}>
        {/* Abstract grid pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(16,185,129,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        <div className="relative z-10 max-w-7xl mx-auto">
          <Link
            to="/engineering/services"
            className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors mb-8"
            data-testid="back-to-studios"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('services.back')}
          </Link>
          <h1
            className="text-4xl sm:text-5xl font-extrabold text-white tracking-tighter"
            style={{fontFamily:'Plus Jakarta Sans,sans-serif'}}
            data-testid="studio-digital-heading"
          >
            {t('eng_v2.digital_title')}
          </h1>
        </div>
      </section>

      {/* AI Pipeline Animation */}
      <AiPipelineAnimation />

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-base text-gray-600 leading-relaxed mb-4">
              {t('services.digital_intro_1')}
            </p>
            <p className="text-base text-gray-600 leading-relaxed mb-12">
              {t('services.digital_intro_2')}
            </p>

            <h2 className="text-xs font-semibold tracking-[0.15em] uppercase text-gray-400 mb-6" style={{fontFamily:'Plus Jakarta Sans,sans-serif'}}>
              {t('services.services_label')}
            </h2>
            <ul className="space-y-4">
              {serviceKeys.map((k) => (
                <li key={k} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-base text-gray-700">{t(`services.${k}`)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-16 border border-emerald-200 bg-emerald-50/50 p-8" data-testid="ai-agents-placeholder">
              <div className="flex items-center gap-3 mb-3">
                <Zap className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-bold text-gray-900" style={{fontFamily:'Plus Jakarta Sans,sans-serif'}}>
                  {t('services.ai_agents_title')}
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                {t('services.ai_agents_desc')}
              </p>
            </div>

            <div className="mt-12">
              <Link
                to="/engineering/contact"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors"
                data-testid="studio-digital-cta"
              >
                {t('services.discuss')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudioDigital;
